import React, { useEffect, useState, useCallback } from 'react';import { Link } from 'react-router-dom';
import { getArticles, getArticleCategories, Article, ArticleCategory } from '../../services/public/publicService';
import { useSeo } from '../../hooks/useSeo';

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'בקרוב';
  const d = new Date(dateStr);
  return d.toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ArticlesPage: React.FC = () => {
  useSeo({
    title: "מאמרים ומדריכים על יבוא רכב ומיסוי | CarTax",
    description:
      "מדריכים ומאמרים על יבוא אישי ומסחרי של רכב, עלויות, מיסים וטיפים — מתעדכן באופן שוטף.",
  });
 const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadCategories = useCallback(async () => {
  try {
    const res = await getArticleCategories();
    if (res.res === 'OK') setCategories(res.categories || []);
  } catch {
    // שגיאת רשת — פשוט לא מציגים קטגוריות
  }
}, []);

const loadArticles = useCallback(async () => {
  setLoading(true);
  try {
    const res = await getArticles({
      category_slug: selectedCategory || undefined,
      page: currentPage,
    });
    if (res.res === 'OK') {
      setArticles(res.articles || []);
      setTotalPages(res.pagination?.total_pages || 1);
    } else {
      setArticles([]);
    }
  } catch {
    setArticles([]);
  }
  setLoading(false);
}, [selectedCategory, currentPage]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleCategoryChange = (slug: string) => {
    setSelectedCategory(slug);
    setCurrentPage(1);
  };

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50">

      {/* Hero */}
      <div className="bg-gradient-to-b from-blue-50 to-white py-16 text-center px-4">
        <h1 className="text-4xl font-bold text-blue-900 mb-4">מאמרים מקצועיים</h1>
        <p className="text-lg text-blue-700 mb-8">מידע מקצועי, עדכונים וטיפים בתחום המכס, מיסוי רכב וייבוא אישי</p>
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/"
            className="flex items-center gap-2 px-6 py-3 border border-blue-900 text-blue-900 rounded-lg hover:bg-blue-50 transition"
          >
            חזרה לעמוד הראשי ←
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">

        {/* קטגוריות */}
        {categories.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">קטגוריות מאמרים</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => handleCategoryChange('')}
                className={`px-5 py-2 rounded-full border transition font-medium ${
                  selectedCategory === ''
                    ? 'bg-blue-900 text-white border-blue-900'
                    : 'bg-white text-blue-900 border-blue-200 hover:border-blue-900'
                }`}
              >
                הכל
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-5 py-2 rounded-full border transition font-medium ${
                    selectedCategory === cat.slug
                      ? 'bg-blue-900 text-white border-blue-900'
                      : 'bg-white text-blue-900 border-blue-200 hover:border-blue-900'
                  }`}
                >
                  {cat.name}
                  <span className="mr-2 text-sm opacity-70">({cat.article_count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* מאמרים */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">טוען מאמרים...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 text-gray-500">אין מאמרים בקטגוריה זו</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map(article => (
              <div key={article.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition">
                {/* תמונת נושא */}
                {article.cover_image_url && (
                  <img
                    src={article.cover_image_url}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-40 object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-6 flex flex-col flex-grow">
                {/* קטגוריה + תאריך */}
                <div className="flex justify-between items-center mb-4">
                  <span className="bg-blue-900 text-white text-xs font-medium px-3 py-1 rounded-full">
                    {article.category_name}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {formatDate(article.published_at)}
                  </span>
                </div>
                {/* כותרת */}
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                  {article.title}
                </h3>
                {/* תקציר */}
                {article.excerpt && (
                  <p className="text-gray-500 text-sm flex-grow mb-4 leading-relaxed">
                    {article.excerpt}
                  </p>
                )}
                {/* Footer */}
                <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-100">
                  <span className="text-gray-400 text-sm">⏱ {article.read_time_minutes} דקות קריאה</span>
                  {article.published_at ? (
                    <Link
                      to={`/articles/${article.slug}`}
                      className="text-blue-700 font-medium text-sm hover:underline"
                      aria-label={`קרא את המאמר: ${article.title}`}
                    >
                      קרא עוד ←
                    </Link>
                  ) : (
                    <span className="text-gray-400 text-sm">בקרוב</span>
                  )}
                </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg border font-medium transition ${
                  currentPage === page
                    ? 'bg-blue-900 text-white border-blue-900'
                    : 'bg-white text-blue-900 border-gray-200 hover:border-blue-900'
                }`}
                aria-label={`עמוד ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;